import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimation, useReducedMotion } from 'framer-motion';
import { Github, Linkedin, Mail, ChevronDown, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
import HeroDotField from '../components/hero/HeroDotField';

export default function Home() {
  const controls = useAnimation();
  const reduceMotion = useReducedMotion();
  const contentRef = useRef(null);
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);

  const roles = [
    { title: 'AI/ML & Full-Stack Engineer', focus: 'Python / PyTorch / React' },
    { title: 'Multi-Agent Systems Architect', focus: 'Baton MCP / Autonomous LLMs' },
    { title: 'Distributed Web & Cloud Systems', focus: 'Node.js / AWS / PostgreSQL' },
  ];

  useEffect(() => {
    controls.start('visible');
  }, [controls]);

  useEffect(() => {
    if (reduceMotion) return;
    const interval = setInterval(() => {
      setCurrentRoleIndex((prev) => (prev + 1) % roles.length);
    }, 3400);
    return () => clearInterval(interval);
  }, [reduceMotion, roles.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.1,
        delayChildren: reduceMotion ? 0 : 0.05,
      },
    },
  };

  const childVariants = {
    hidden: { y: reduceMotion ? 0 : 14, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: reduceMotion ? 0 : 0.45, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const techStack = [
    'React / Vite',
    'TypeScript',
    'Python',
    'PyTorch / AI',
    'Node.js',
    'PostgreSQL',
    'AWS / Docker',
  ];

  const socials = [
    { Icon: Github, href: 'https://github.com/Rakshan001', label: 'GitHub' },
    {
      Icon: Linkedin,
      href: 'https://linkedin.com/in/rakshan-shetty-953864225',
      label: 'LinkedIn',
    },
    { Icon: Mail, href: 'mailto:rakshanshetty2003@gmail.com', label: 'Email' },
  ];

  const scrollToSection = (e, selector) => {
    e.preventDefault();
    const element = document.querySelector(selector);
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - 64;
    window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  return (
    <section
      id="home-hero"
      className="relative isolate flex min-h-[calc(100svh-64px)] flex-col justify-center overflow-hidden"
    >
      <HeroDotField contentRef={contentRef} />

      <div className="relative mx-auto flex w-full max-w-[1200px] flex-1 items-center justify-center px-6 py-16 sm:py-20">
        <motion.div
          ref={contentRef}
          className="mx-auto w-full max-w-3xl space-y-7 text-center"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {/* Eyebrow Status Pill */}
          <motion.div variants={childVariants} className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] shadow-[var(--shadow-sm)] text-xs text-[var(--fg-secondary)]">
              <span className="font-medium text-[var(--fg-primary)]">Available for Engineering Roles</span>
              <span className="text-[var(--fg-muted)]">/</span>
              <span className="t-mono text-[11px] text-[var(--fg-muted)]">AIML 2025</span>
            </div>
          </motion.div>

          {/* Main Headline & Animated Role Cycler */}
          <div className="space-y-3">
            <motion.h1
              variants={childVariants}
              className="t-display tracking-tight text-[var(--fg-primary)] text-4xl sm:text-6xl md:text-7xl font-semibold"
            >
              Rakshan Shetty
            </motion.h1>

            <motion.div variants={childVariants} className="min-h-[34px] flex items-center justify-center relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentRoleIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-flex items-center gap-2 text-sm sm:text-base font-medium text-[var(--fg-secondary)]"
                >
                  <span className="text-[var(--accent)] font-semibold">{roles[currentRoleIndex].title}</span>
                  <span className="t-mono text-xs text-[var(--fg-muted)] hidden sm:inline-block">
                    [{roles[currentRoleIndex].focus}]
                  </span>
                </motion.div>
              </AnimatePresence>
            </motion.div>

            <motion.p
              variants={childVariants}
              className="mx-auto max-w-xl text-sm sm:text-base md:text-lg text-[var(--fg-secondary)] leading-relaxed"
            >
              AIML B.E graduate (2025) building fast, modern web applications, multi-agent AI orchestration, and production-grade software architectures.
            </motion.p>
          </div>

          {/* Call to Actions */}
          <motion.div
            variants={childVariants}
            className="flex flex-wrap items-center justify-center gap-3 pt-1"
          >
            <Button asChild variant="primary" size="lg" className="group">
              <a href="mailto:rakshanshetty2003@gmail.com" className="flex items-center gap-2">
                <Mail size={16} />
                <span>Let&apos;s Connect</span>
              </a>
            </Button>

            <Button asChild variant="secondary" size="lg" className="group">
              <a
                href="#projects"
                onClick={(e) => scrollToSection(e, '#projects')}
                className="flex items-center gap-2"
              >
                <span>Explore Work</span>
                <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
            </Button>
          </motion.div>


          {/* Tech Stack Pills Strip */}
          <motion.div
            variants={childVariants}
            className="flex flex-wrap justify-center items-center gap-1.5 max-w-2xl mx-auto pt-1"
          >
            <span className="t-mono text-[11px] text-[var(--fg-muted)] mr-1 hidden sm:inline-block">
              Core Stack:
            </span>
            {techStack.map((tech) => (
              <motion.span
                key={tech}
                whileHover={reduceMotion ? undefined : { y: -2 }}
                transition={{ duration: 0.15 }}
                className="t-mono text-[11px] px-2.5 py-1 rounded-md bg-[var(--bg-elevated)] border border-[var(--bg-recessed)] text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:border-[var(--border-ring)] transition-colors shadow-[var(--shadow-sm)] cursor-default"
              >
                {tech}
              </motion.span>
            ))}
          </motion.div>

          {/* Social Links Bar */}
          <motion.div variants={childVariants} className="flex justify-center items-center gap-2 pt-1">
            {socials.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                className="inline-flex items-center gap-1.5 cursor-pointer rounded-md bg-[var(--bg-elevated)] px-3 py-1.5 text-xs text-[var(--fg-secondary)] shadow-[var(--shadow-sm)] border border-[var(--bg-recessed)] transition-all hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)] hover:border-[var(--border-ring)] focus-visible:shadow-[var(--focus-ring)] focus-visible:outline-none"
                target={href.startsWith('mailto:') ? undefined : '_blank'}
                rel={href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                aria-label={label}
              >
                <Icon size={14} />
                <span className="font-medium">{label}</span>
              </a>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Floating Animated Scroll Cue */}
      <motion.a
        href="#about"
        onClick={(e) => scrollToSection(e, '#about')}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: reduceMotion ? 0 : 0.8, duration: 0.6 }}
        className="group absolute inset-x-0 bottom-4 sm:bottom-5 mx-auto flex w-fit cursor-pointer flex-col items-center gap-1 rounded-md px-3 py-1.5 text-[var(--fg-muted)] transition-colors hover:text-[var(--fg-secondary)] focus-visible:shadow-[var(--focus-ring)] focus-visible:outline-none"
      >
        <span className="t-mono text-[10px] tracking-widest uppercase">Scroll</span>
        <motion.div
          animate={reduceMotion ? undefined : { y: [0, 4, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </motion.div>
      </motion.a>
    </section>
  );
}
